import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'

export const NFL_TZ = 'America/New_York'

/* ── Server clock ─────────────────────────────────────────────────────────────
   Never lock or unlock picks against the device clock. Someone's phone will be
   four minutes fast and they will blame you, not their phone. We measure the
   offset once at boot and render every countdown against it. The database is
   still the authority — this only keeps the UI honest. */
let serverOffsetMs = 0

export function setServerOffset(serverNow: Date) {
  serverOffsetMs = serverNow.getTime() - Date.now()
}

export function serverNow(): Date {
  return new Date(Date.now() + serverOffsetMs)
}

export function isLocked(locksAt: string | null): boolean {
  // A week with no schedule loaded is closed, not open. Nothing to pick yet.
  if (!locksAt) return true
  return serverNow().getTime() >= new Date(locksAt).getTime()
}

export function msUntil(iso: string): number {
  return Math.max(0, new Date(iso).getTime() - serverNow().getTime())
}

/** "2d 04:11:36" / "04:11:36" / "11:36" — mono-safe, fixed width per tier. */
export function formatCountdown(ms: number): string {
  const total = Math.floor(ms / 1000)
  const d = Math.floor(total / 86400)
  const h = Math.floor((total % 86400) / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`
  return `${pad(m)}:${pad(s)}`
}

/** "SUN 1:00 PM ET" — every kickoff renders in league time, not device time.
    The crew is spread across zones; one shared clock avoids arguments. */
export function kickoffLabel(iso: string): string {
  return `${format(new TZDate(iso, NFL_TZ), 'EEE h:mm a').toUpperCase()} ET`
}

export function lockLabel(iso: string): string {
  return `${format(new TZDate(iso, NFL_TZ), 'EEE MMM d, h:mm a').toUpperCase()} ET`
}

/** "THURSDAY, SEP 10" — the day banner over a slate of games. */
export function dayHeading(iso: string): string {
  return format(new TZDate(iso, NFL_TZ), 'EEEE, MMM d').toUpperCase()
}

/** "10:00 AM ET" — a kickoff slot. Games sharing a kickoff share a slot.

    The zone is spelled out because most of the crew isn't in it. An unlabeled
    "7:00 PM" reads as local time to someone in Phoenix, and they'd think they
    had three more hours before picks lock. */
export function slotTime(iso: string): string {
  return `${format(new TZDate(iso, NFL_TZ), 'h:mm a')} ET`
}

/** Grouping key for "which day is this game on", in league time.
    Grouping on the raw ISO string instead would split a Sunday night 8:20pm ET
    kickoff into its own Monday group, because that instant is already Monday in
    UTC. Every heading in this app is ET, so the key has to be ET too. */
export function etDayKey(iso: string): string {
  return format(new TZDate(iso, NFL_TZ), 'yyyy-MM-dd')
}

const WORDS = [
  'ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
  'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN',
  'SEVENTEEN', 'EIGHTEEN',
]

/** Title cards spell the week out. "WEEK 7" is a spreadsheet; "WEEK SEVEN"
    is a title card.

    Reads `weeks.label`, the real column — there is no `weeks.kind`, and the
    postseason values are not slugs. The database stores display strings:
    "Week 1"…"Week 18", "Wild Card", "Divisional", "Conference Championship",
    "Super Bowl", plus "Hall of Fame Game" and "Preseason Week 1"…"3" on the
    preseason season. Anything that isn't a plain "Week N" already reads as a
    title, so it passes through uppercased instead of being mapped. */
export function weekTitle(weekNumber: number, label?: string | null): string {
  if (label && !/^week\s+\d+$/i.test(label.trim())) return label.toUpperCase()
  return `WEEK ${WORDS[weekNumber] ?? weekNumber}`
}
